import React, { forwardRef } from 'react';
import {
  FormHelperText as MuiFormHelperText,
  FormHelperTextProps as MuiFormHelperTextProps,
} from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { extractPounceProps } from '../../utils/extractPounceProps';
import { mapPouncePropsToSx } from '../../utils/pouncePropsMapper';
import { PounceStyleProps } from '../../utils';

// Re-export MUI types
export type { FormHelperTextProps as MuiFormHelperTextProps } from '@mui/material';

export interface FormHelperTextProps
  extends Omit<MuiFormHelperTextProps, keyof PounceStyleProps>, PounceStyleProps {
  /** Required ID for aria-describedby association */
  id: string;

  /** The helper text content */
  children?: React.ReactNode;

  /** Additional styles to apply (combined with pounce props) */
  sx?: MuiFormHelperTextProps['sx'];
}

/**
 * FormHelperText
 *
 * A utility component that quickly allows you to add helpful messages to form fields.
 */
export const FormHelperText = forwardRef<HTMLParagraphElement, FormHelperTextProps>(
  function FormHelperText({ children, sx, ...rest }, ref) {
    const { pounceProps, otherProps } = extractPounceProps(rest);
    const pounceStyles = mapPouncePropsToSx(pounceProps);

    const combinedSx = {
      ...pounceStyles,
      ...sx,
    } as SxProps<Theme>;

    return (
      <MuiFormHelperText ref={ref} sx={combinedSx} {...otherProps}>
        {children}
      </MuiFormHelperText>
    );
  },
);

FormHelperText.displayName = 'FormHelperText';

export default FormHelperText;
